import React, { Fragment, useMemo, useState } from 'react';
import { Theme, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { ColumnDef, getAllColumns, isColumnApplicable, NAME_COLUMN, sessionKey } from '../activity-import-columns';
import { ImportActivityRow, RowErrors } from '../activity-import-types';
import { getRowSessionCount, parseActivityType } from '../activity-import-validation';
import { GridCell } from './GridCell';
import { getRowStatus, StatusIcon } from './status-icon';

const STATUS_WIDTH = 44;

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto',
    backgroundColor: '#ffffff',
    border: `1px solid ${theme.color.grey.border}`,
    borderRadius: 6,
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
  },
  headerCell: {
    position: 'sticky',
    zIndex: 2,
    padding: '0 10px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRight: `1px solid ${theme.color.grey.border}`,
    borderBottom: `1px solid ${theme.color.grey.border}`,
  },
  groupHeader: {
    top: 0,
    height: 30,
    fontSize: '13px',
    fontWeight: 600,
    color: theme.color.grey.neutral500,
    backgroundColor: '#ffffff',
  },
  columnHeader: {
    top: 30,
    height: 30,
    fontSize: '12px',
    fontWeight: 600,
    color: theme.color.grey.neutral300,
    backgroundColor: theme.color.white.neutral50,
  },
  groupToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:focus-visible': {
      outline: `2px solid ${theme.color.secondary.main900}`,
    },
  },
  pinStatus: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    width: STATUS_WIDTH,
    minWidth: STATUS_WIDTH,
    maxWidth: STATUS_WIDTH,
    padding: 0,
    textAlign: 'center',
  },
  pinName: {
    position: 'sticky',
    left: STATUS_WIDTH,
    zIndex: 1,
    fontWeight: 600,
    boxShadow: '4px 0 6px -4px rgba(0, 0, 0, 0.12)',
  },
  pinnedHeader: {
    zIndex: 3,
  },
  row: {
    backgroundColor: '#ffffff',
  },
  createdRow: {
    backgroundColor: '#F6FBF6',
  },
  selectedRow: {
    backgroundColor: theme.color.secondary.main50,
  },
  statusCell: {
    borderRight: `1px solid ${theme.color.grey.border}`,
    borderBottom: `1px solid ${theme.color.grey.border}`,
    height: 36,
    boxSizing: 'border-box',
    backgroundColor: 'inherit',
  },
  statusButton: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    '&:hover svg': {
      transform: 'scale(1.15)',
    },
    '& svg': {
      transition: 'transform 120ms',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.color.secondary.main900}`,
      outlineOffset: -2,
    },
  },
}));

interface Props {
  rows: ImportActivityRow[];
  errors: Record<string, RowErrors>;
  sessionCount: number;
  programNames: string[];
  reportTypeNames: string[];
  selectedRowId?: string;
  locked?: boolean;
  onChange: (rowId: string, key: string, value: string) => void;
  onSelectRow: (rowId: string) => void;
  onAddressClick: (rowId: string) => void;
}

interface ColumnGroup {
  name: string;
  columns: ColumnDef[];
}

export function ActivitiesGrid({
  rows,
  errors,
  sessionCount,
  programNames,
  reportTypeNames,
  selectedRowId,
  locked,
  onChange,
  onSelectRow,
  onAddressClick,
}: Props) {
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => {
    const result: ColumnGroup[] = [];
    getAllColumns(sessionCount)
      .filter((column) => column.key !== NAME_COLUMN.key)
      .forEach((column) => {
        const last = result[result.length - 1];
        if (last && last.name === column.group) last.columns.push(column);
        else result.push({ name: column.group, columns: [column] });
      });
    return result.map((group) => ({
      ...group,
      visibleColumns: collapsed[group.name] ? group.columns.slice(0, 1) : group.columns,
    }));
  }, [sessionCount, collapsed]);

  const toggleGroup = (name: string) => setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div className={classes.wrapper}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th
              className={`${classes.headerCell} ${classes.groupHeader} ${classes.pinnedHeader} ${classes.pinStatus}`}
            />
            <th
              className={`${classes.headerCell} ${classes.groupHeader} ${classes.pinnedHeader} ${classes.pinName}`}
              style={{ width: NAME_COLUMN.width, minWidth: NAME_COLUMN.width }}
            />
            {groups.map((group) => (
              <th
                key={group.name}
                className={`${classes.headerCell} ${classes.groupHeader}`}
                colSpan={group.visibleColumns.length}
              >
                {group.columns.length > 1 ? (
                  <button type='button' className={classes.groupToggle} onClick={() => toggleGroup(group.name)}>
                    {group.name}
                    {collapsed[group.name] ? (
                      <ChevronRight style={{ fontSize: 16 }} />
                    ) : (
                      <ChevronLeft style={{ fontSize: 16 }} />
                    )}
                  </button>
                ) : (
                  group.name
                )}
              </th>
            ))}
          </tr>
          <tr>
            <th
              className={`${classes.headerCell} ${classes.columnHeader} ${classes.pinnedHeader} ${classes.pinStatus}`}
            />
            <th
              className={`${classes.headerCell} ${classes.columnHeader} ${classes.pinnedHeader} ${classes.pinName}`}
              style={{ width: NAME_COLUMN.width, minWidth: NAME_COLUMN.width }}
            >
              {NAME_COLUMN.header}
            </th>
            {groups.map((group) => (
              <Fragment key={group.name}>
                {group.visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`${classes.headerCell} ${classes.columnHeader}`}
                    style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}
                  >
                    {column.header}
                  </th>
                ))}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowErrors = errors[row.id] ?? {};
            const activityType = parseActivityType(row.values.activityType);
            const readOnly =
              locked || row.createStatus === 'created' || row.createStatus === 'creating' || Boolean(row.activityId);
            const status = getRowStatus(row, Object.keys(rowErrors).length);
            const rowSessions = getRowSessionCount(row);
            const sessionState = (column: ColumnDef) => {
              if (!column.session || column.session <= rowSessions) return 'used';
              if (!readOnly && column.session === rowSessions + 1 && column.key === sessionKey(column.session, '')) {
                return 'next';
              }
              return 'unused';
            };
            const rowClassName = [
              classes.row,
              row.createStatus === 'created' ? classes.createdRow : '',
              row.id === selectedRowId ? classes.selectedRow : '',
            ].join(' ');

            return (
              <tr key={row.id} className={rowClassName}>
                <td className={`${classes.pinStatus} ${classes.statusCell}`}>
                  <Tooltip title={`${status.label}. Click to open details`} placement='right'>
                    <button type='button' className={classes.statusButton} onClick={() => onSelectRow(row.id)}>
                      <StatusIcon kind={status.kind} />
                    </button>
                  </Tooltip>
                </td>
                <GridCell
                  rowId={row.id}
                  column={NAME_COLUMN}
                  value={row.values[NAME_COLUMN.key] ?? ''}
                  error={rowErrors[NAME_COLUMN.key]}
                  applicable
                  readOnly={readOnly}
                  programNames={programNames}
                  reportTypeNames={reportTypeNames}
                  className={classes.pinName}
                  onChange={onChange}
                  onAddressClick={onAddressClick}
                />
                {groups.map((group) => (
                  <Fragment key={group.name}>
                    {group.visibleColumns.map((column) => {
                      const state = sessionState(column);
                      return (
                        <GridCell
                          key={column.key}
                          rowId={row.id}
                          column={column}
                          value={row.values[column.key] ?? ''}
                          error={rowErrors[column.key]}
                          applicable={state !== 'unused' && (!activityType || isColumnApplicable(column, activityType))}
                          inapplicableReason={state === 'unused' ? 'Not used by this activity' : undefined}
                          placeholder={state === 'next' ? `+ Add session ${column.session}` : undefined}
                          readOnly={readOnly}
                          geocodeStatus={column.kind === 'address' ? row.geocodeStatus : undefined}
                          programNames={programNames}
                          reportTypeNames={reportTypeNames}
                          onChange={onChange}
                          onAddressClick={onAddressClick}
                        />
                      );
                    })}
                  </Fragment>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
